import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { purchasingService } from "../services/dataService";
import { getStocks } from "../services/api";
import { PurchaseLineUpsert, PurchaseOrderDetail, PurchaseOrderStatus, Stock, Supplier } from "../types";
import { formatCurrency } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import { todayLocal } from "../utils/dates";
import { useSettings } from "../context/SettingsContext";
import PageLoader from "../components/PageLoader";
import ReceiveModal from "../components/ReceiveModal";
import { PO_STATUS_LABEL } from "./Purchasing";
import "./Receivables.css";
import "./Purchasing.css";
import "../components/SendDocumentModal.css";

interface EditableLine { stockId: string; description: string; quantity: string; unitPrice: string; vatRate: number; }

const today = () => todayLocal();
const toInput = (iso?: string | null) => (iso ? iso.slice(0, 10) : "");
const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "—");
const fmtQty = (n: number) => n.toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const num = (s: string) => { const n = parseFloat((s || "").replace(",", ".")); return isNaN(n) ? 0 : n; };

/**
 * Satın alma siparişi: /purchasing/new → taslak oluştur; /purchasing/:id → taslaksa düzenle,
 * değilse belge görünümü (iletildi / mal kabul / iptal aksiyonları).
 */
const PurchaseOrderForm: React.FC = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { has, hasModule } = usePermissions();
  const { settings } = useSettings();

  const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [receiveOpen, setReceiveOpen] = useState(false);

  // form
  const [supplierId, setSupplierId] = useState("");
  const [orderDate, setOrderDate] = useState(today());
  const [expectedDate, setExpectedDate] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<EditableLine[]>([{ stockId: "", description: "", quantity: "1", unitPrice: "", vatRate: settings?.defaultVatRate ?? 20 }]);

  const vatOptions = useMemo(() => {
    const p = (settings?.vatRates || "").split(",").map((x) => parseFloat(x.trim().replace(",", "."))).filter((n) => !isNaN(n));
    return p.length ? p : [0, 1, 10, 20];
  }, [settings?.vatRates]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [sup, st] = await Promise.all([purchasingService.suppliers(false), getStocks().then((r) => r.data as Stock[]).catch(() => [] as Stock[])]);
      setSuppliers(sup); setStocks(st);
      if (!isNew && id) {
        const o = await purchasingService.order(id);
        setOrder(o);
        setSupplierId(o.supplierId); setOrderDate(toInput(o.orderDate)); setExpectedDate(toInput(o.expectedDate));
        setCurrency(o.currency); setNote(o.note || "");
        setLines(o.lines.map((l) => ({ stockId: l.stockId, description: l.description || "", quantity: String(l.quantity), unitPrice: String(l.unitPrice), vatRate: l.vatRate })));
      }
    } catch (e: any) { setError(e?.response?.data?.message || (typeof e?.response?.data === "string" ? e.response.data : "Yüklenemedi.")); }
    finally { setLoading(false); }
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  // tedarikçi seçilince para birimi öner
  useEffect(() => {
    if (isNew && supplierId) { const s = suppliers.find((x) => x.supplierId === supplierId); if (s) setCurrency(s.currency); }
  }, [supplierId, suppliers, isNew]);

  const editable = isNew || order?.status === PurchaseOrderStatus.Draft;
  const stockOf = (sid: string) => stocks.find((s) => s.id === sid);

  const totals = useMemo(() => {
    let net = 0, vat = 0;
    for (const l of lines) { const n = num(l.quantity) * num(l.unitPrice); net += n; vat += n * l.vatRate / 100; }
    return { net, vat, gross: net + vat };
  }, [lines]);

  const updLine = (i: number, patch: Partial<EditableLine>) => setLines((p) => p.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const pickStock = (i: number, sid: string) => {
    const s = stockOf(sid);
    updLine(i, { stockId: sid, unitPrice: s && (!lines[i].unitPrice || lines[i].unitPrice === "0") && s.unitPrice ? String(s.unitPrice) : lines[i].unitPrice });
  };

  const buildDto = () => ({
    supplierId, orderDate, expectedDate: expectedDate || null, currency, note: note.trim() || null,
    lines: lines.map<PurchaseLineUpsert>((l) => ({ stockId: l.stockId, description: l.description.trim() || null, quantity: num(l.quantity), unitPrice: num(l.unitPrice), vatRate: l.vatRate })),
  });

  const validate = () => {
    if (!supplierId) return "Tedarikçi seçin.";
    if (lines.length === 0) return "En az bir satır ekleyin.";
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.stockId) return `Satır ${i + 1}: stok seçin.`;
      if (num(l.quantity) <= 0) return `Satır ${i + 1}: miktar sıfırdan büyük olmalı.`;
      if (num(l.unitPrice) < 0) return `Satır ${i + 1}: birim fiyat negatif olamaz.`;
    }
    return null;
  };

  const run = async (fn: () => Promise<void>) => {
    setSaving(true); setError("");
    try { await fn(); }
    catch (e: any) { const d = e?.response?.data; setError(typeof d === "string" ? d : d?.message || "İşlem yapılamadı."); }
    finally { setSaving(false); }
  };

  const save = (thenOrder = false) => {
    const v = validate(); if (v) return setError(v);
    run(async () => {
      if (isNew) {
        const r = await purchasingService.createOrder(buildDto());
        if (thenOrder) await purchasingService.markOrdered(r.purchaseOrderId);
        navigate(`/purchasing/${r.purchaseOrderId}`, { replace: true });
      } else if (id) {
        await purchasingService.updateOrder(id, buildDto());
        if (thenOrder) await purchasingService.markOrdered(id);
        await load();
      }
    });
  };
  const markOrdered = () => id && run(async () => { await purchasingService.markOrdered(id); await load(); });
  const cancel = () => {
    const reason = window.prompt("İptal sebebi (zorunlu):");
    if (!reason || reason.trim().length < 3) return;
    id && run(async () => { await purchasingService.cancel(id, reason.trim()); await load(); });
  };
  const remove = () => id && window.confirm("Taslak sipariş silinsin mi?") && run(async () => { await purchasingService.deleteOrder(id); navigate("/purchasing"); });

  if (loading) return <PageLoader message="Sipariş yükleniyor..." />;

  const canReceive = order && (order.status === PurchaseOrderStatus.Ordered || order.status === PurchaseOrderStatus.PartiallyReceived) && has("purchasing:orders:update");
  const supplier = suppliers.find((s) => s.supplierId === supplierId) || null;

  return (
    <div className="rc-page">
      <Link className="rc-crumb" to="/purchasing">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
        Satın Alma
      </Link>

      <div className="pu-head">
        <div>
          <h1>
            {isNew ? "Yeni Satın Alma Siparişi" : order?.number}
            {order && <span className={`pu-status s${order.status}`}>{PO_STATUS_LABEL[order.status]}</span>}
            {order?.isOverdue && <span className="rc-chip danger">Teslimi gecikti</span>}
          </h1>
          <div className="pu-head-meta">
            {order ? <>Oluşturan <b>{order.createdBy || "—"}</b> · {fmtDate(order.orderDate)}{order.orderedAt ? <> · İletildi {fmtDate(order.orderedAt)}</> : null}{order.cancelReason ? <> · İptal: <b>{order.cancelReason}</b></> : null}</> : "Taslak olarak kaydedin ya da doğrudan tedarikçiye iletin."}
          </div>
          <div className="rc-head-actions" style={{ marginTop: "0.875rem" }}>
            {editable && has(isNew ? "purchasing:orders:create" : "purchasing:orders:update") && (
              <>
                <button className="rc-btn primary" onClick={() => save(true)} disabled={saving}>Kaydet ve İlet</button>
                <button className="rc-btn" onClick={() => save(false)} disabled={saving}>{isNew ? "Taslak Kaydet" : "Taslağı Kaydet"}</button>
              </>
            )}
            {!isNew && order?.status === PurchaseOrderStatus.Draft && has("purchasing:orders:update") && (
              <button className="rc-btn" onClick={markOrdered} disabled={saving}>Tedarikçiye İlet</button>
            )}
            {canReceive && <button className="rc-btn primary" onClick={() => setReceiveOpen(true)} disabled={saving}>Mal Kabul</button>}
            {!isNew && order && (order.status === PurchaseOrderStatus.Draft || order.status === PurchaseOrderStatus.Ordered) && has("purchasing:orders:update") && (
              <button className="rc-btn danger-ghost" onClick={cancel} disabled={saving}>İptal Et</button>
            )}
            {!isNew && order?.status === PurchaseOrderStatus.Draft && has("purchasing:orders:delete") && (
              <button className="rc-btn danger-ghost" onClick={remove} disabled={saving}>Sil</button>
            )}
          </div>
        </div>
        {order && (
          <div className="pu-info">
            <div className="pu-info-item"><div className="pu-info-label">Tutar (KDV dahil)</div><div className="pu-info-value">{formatCurrency(order.grossTotal, order.currency)}</div></div>
            <div className={`pu-info-item ${order.isOverdue ? "warn" : ""}`}><div className="pu-info-label">Beklenen Teslim</div><div className="pu-info-value">{fmtDate(order.expectedDate)}</div></div>
            <div className="pu-info-item"><div className="pu-info-label">Teslimat</div><div className="pu-info-value">{fmtQty(order.receivedQuantity)} / {fmtQty(order.orderedQuantity)}</div></div>
          </div>
        )}
      </div>

      {error && <div className="ic-alert error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {/* ── Belge ── */}
      <div className="rc-card"><div className="pu-doc">
        <div className="pu-doc-head">
          <div className="pu-field">
            <label>Tedarikçi</label>
            {editable ? (
              <select className="ic-in" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">Seçin…</option>
                {suppliers.map((s) => <option key={s.supplierId} value={s.supplierId}>{s.name}</option>)}
              </select>
            ) : <div className="ic-in" style={{ background: "#F4F7FB" }}>{order?.supplierName}</div>}
            {supplier && <div className="pu-stock-hint">{[supplier.contactPerson, supplier.phone, supplier.email].filter(Boolean).join(" · ") || "İletişim bilgisi yok"} · Vade {supplier.paymentTermDays} gün</div>}
          </div>
          <div className="pu-field"><label>Sipariş Tarihi</label>{editable ? <input type="date" className="ic-in" value={orderDate} max={today()} onChange={(e) => setOrderDate(e.target.value)} /> : <div className="ic-in" style={{ background: "#F4F7FB" }}>{fmtDate(order?.orderDate)}</div>}</div>
          <div className="pu-field"><label>Beklenen Teslim</label>{editable ? <input type="date" className="ic-in" value={expectedDate} min={orderDate} onChange={(e) => setExpectedDate(e.target.value)} /> : <div className="ic-in" style={{ background: "#F4F7FB" }}>{fmtDate(order?.expectedDate)}</div>}</div>
          <div className="pu-field"><label>Para Birimi</label>{editable ? (
            <select className="ic-in" value={currency} onChange={(e) => setCurrency(e.target.value)}><option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option></select>
          ) : <div className="ic-in" style={{ background: "#F4F7FB" }}>{order?.currency}</div>}</div>
        </div>

        <div className="rc-table-wrap">
          <table className="pu-table">
            <thead><tr>
              <th className="c-no">#</th><th className="c-stock">Stok</th><th className="num c-qty">Miktar</th><th className="c-unit">Birim</th>
              <th className="num c-price">Birim Fiyat</th><th className="c-vat">KDV</th>
              {!editable && <th className="num c-recv">Alınan</th>}
              <th className="num c-amt">Tutar</th>{editable && <th className="c-act" />}
            </tr></thead>
            <tbody>
              {lines.map((l, i) => {
                const s = stockOf(l.stockId);
                const n = num(l.quantity) * num(l.unitPrice);
                const src = order?.lines[i];
                return (
                  <tr key={i}>
                    <td className="c-no">{i + 1}</td>
                    <td className="c-stock">
                      {editable ? (
                        <>
                          <select className="ic-in" value={l.stockId} onChange={(e) => pickStock(i, e.target.value)}>
                            <option value="">Stok seçin…</option>
                            {stocks.map((st) => <option key={st.id} value={st.id}>{st.productCode ? `${st.productCode} — ` : ""}{st.productName} ({st.unit})</option>)}
                          </select>
                          <input className="ic-in ic-in-sub" value={l.description} placeholder="Açıklama (isteğe bağlı)" onChange={(e) => updLine(i, { description: e.target.value })} />
                          {s && hasModule("stock") && <div className="pu-stock-hint">Mevcut stok <b>{fmtQty(s.currentStock)} {s.unit}</b>{s.unitPrice ? <> · Son fiyat <b>{formatCurrency(s.unitPrice, s.currency || "TRY")}</b></> : null}{s.supplier ? <> · {s.supplier}</> : null}</div>}
                        </>
                      ) : (
                        <div style={{ paddingTop: "0.25rem" }}><b>{src?.stockName}</b>{src?.stockCode ? <span className="pu-stock-hint"> · {src.stockCode}</span> : null}{src?.description && <div className="pu-stock-hint">{src.description}</div>}</div>
                      )}
                    </td>
                    <td className="num c-qty">{editable ? <input className="ic-in num" inputMode="decimal" value={l.quantity} onChange={(e) => updLine(i, { quantity: e.target.value })} /> : <span style={{ display: "block", paddingTop: "0.25rem" }}>{fmtQty(num(l.quantity))}</span>}</td>
                    <td className="c-unit">{s?.unit || src?.unit || "—"}</td>
                    <td className="num c-price">{editable ? <input className="ic-in num" inputMode="decimal" value={l.unitPrice} placeholder="0,00" onChange={(e) => updLine(i, { unitPrice: e.target.value })} /> : <span style={{ display: "block", paddingTop: "0.25rem" }}>{formatCurrency(num(l.unitPrice), currency)}</span>}</td>
                    <td className="c-vat">{editable ? (
                      <select className="ic-in" value={l.vatRate} onChange={(e) => updLine(i, { vatRate: parseFloat(e.target.value) })}>{vatOptions.map((r) => <option key={r} value={r}>%{r}</option>)}</select>
                    ) : <span style={{ display: "block", paddingTop: "0.25rem" }}>%{l.vatRate}</span>}</td>
                    {!editable && <td className="num c-recv" style={{ paddingTop: "0.75rem" }}>{src ? <>{fmtQty(src.receivedQuantity)}{src.remaining > 0 ? <span className="pu-recv-remaining"> (kalan {fmtQty(src.remaining)})</span> : <span className="rc-chip ok" style={{ marginLeft: "0.375rem" }}>Tam</span>}</> : "—"}</td>}
                    <td className="num c-amt"><b>{n > 0 ? formatCurrency(n, currency) : "—"}</b></td>
                    {editable && <td className="c-act">{lines.length > 1 && <button className="ic-rm" title="Satırı kaldır" onClick={() => setLines((p) => p.filter((_, k) => k !== i))}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {editable && (
          <button className="ic-add" type="button" onClick={() => setLines((p) => [...p, { stockId: "", description: "", quantity: "1", unitPrice: "", vatRate: settings?.defaultVatRate ?? 20 }])}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg> Satır ekle
          </button>
        )}

        <div className="pu-totals">
          <div className="pu-field full">
            <label>Not</label>
            {editable ? <textarea className="ic-in ic-textarea" rows={3} value={note} maxLength={500} placeholder="Tedarikçiye not, teslimat koşulu…" onChange={(e) => setNote(e.target.value)} />
              : <div className="ic-in" style={{ background: "#F4F7FB", minHeight: "3rem" }}>{order?.note || "—"}</div>}
          </div>
          <div>
            <div className="pu-total-row"><span>Ara Toplam</span><span>{formatCurrency(totals.net, currency)}</span></div>
            <div className="pu-total-row"><span>KDV</span><span>{formatCurrency(totals.vat, currency)}</span></div>
            <div className="pu-total-row grand"><span>Genel Toplam</span><span>{formatCurrency(totals.gross, currency)}</span></div>
          </div>
        </div>
      </div></div>

      {/* ── Mal kabul geçmişi ── */}
      {order && order.receipts.length > 0 && (
        <div className="rc-card">
          <div className="rc-card-head"><div><h2>Mal Kabul Geçmişi</h2><p>Her teslimat ayrı kayıt; stok modülü açıkken giriş hareketi üretildi.</p></div><span className="rc-chip neutral">{order.receipts.length} teslimat</span></div>
          <div className="rc-table-wrap">
            <table className="rc-table">
              <thead><tr><th>Tarih</th><th>İrsaliye</th><th>Kalemler</th><th>Teslim Alan</th><th>Not</th></tr></thead>
              <tbody>
                {order.receipts.map((r) => (
                  <tr key={r.purchaseReceiptId}>
                    <td>{fmtDate(r.receiptDate)}</td>
                    <td>{r.waybillNo || "—"}</td>
                    <td>{r.lines.map((l) => `${l.stockName}: ${fmtQty(l.quantity)} ${l.unit}${l.stockMovementCreated ? "" : " (stok hareketi yok)"}`).join(" · ")}</td>
                    <td className="muted">{r.receivedBy || "—"}</td>
                    <td className="muted">{r.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {receiveOpen && order && <ReceiveModal order={order} onClose={() => setReceiveOpen(false)} onSaved={load} />}
    </div>
  );
};

export default PurchaseOrderForm;
